import React, { useEffect, useState } from 'react';
import { Badge, Button, Spin, Empty, Avatar } from 'antd';
import { BellIcon } from '@phosphor-icons/react';
import { Popover } from './Popover';
import { useNotifications } from '../hooks/useNotifications';
import { CustomScrollbar } from './CustomScrollbar';
import { formatRelativeTime } from '../utils/time';
import type { SquareComment } from '../types';
import '../../styles/NotificationCenter.css';

export interface NotificationCenterProps {
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** 点击通知项的回调 */
  onNotificationClick?: (notification: SquareComment) => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isLoggedIn,
  onNotificationClick,
}) => {
  const {
    unreadCount,
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications({ isLoggedIn });

  const [open, setOpen] = useState(false);

  // 打开时加载通知列表（仅在无缓存时）
  useEffect(() => {
    if (open && isLoggedIn && notifications.length === 0) {
      fetchNotifications();
    }
  }, [open, isLoggedIn, notifications.length, fetchNotifications]);

  const handleNotificationClick = (notification: SquareComment) => {
    markAsRead(notification.id);
    onNotificationClick?.(notification);
    setOpen(false);
  };

  const content = (
    <div className="notification-panel">
      {/* 头部 */}
      <div className="notification-header">
        <span>通知</span>
        {notifications.length > 0 && (
          <Button type="text" onClick={markAllAsRead}>
            全部已读
          </Button>
        )}
      </div>

      {/* 列表区域 */}
      <div className="notification-list-container">
        {loading ? (
          <div className="notification-loading">
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            description="暂无新通知"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="notification-empty"
          />
        ) : (
          <div className="notification-list">
            {notifications.map((item) => (
              <div
                key={item.id}
                className="notification-item"
                onClick={() => handleNotificationClick(item)}
              >
                <Avatar src={item.user?.avatarUrl} size={36} className="notification-avatar">
                  {item.user?.username?.[0]}
                </Avatar>
                <div className="notification-item-content">
                  <div className="notification-item-title">
                    <strong>{item.user?.username || '用户'}</strong>
                    <span> 评论了你的帖子</span>
                  </div>
                  <div className="notification-text">{item.content}</div>
                  <div className="notification-time">
                    {formatRelativeTime(item.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // 未登录不显示
  if (!isLoggedIn) {
    return null;
  }

  return (
    <Popover
      content={content}
      open={open}
      onOpenChange={setOpen}
      placement="topRight"
      contentClassName="notification-popover"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]} className="notification-badge">
        <Button
          type="text"
          icon={<BellIcon size={24} weight={unreadCount > 0 ? 'regular' : 'regular'} />}
          className="notification-trigger"
        />
      </Badge>
    </Popover>
  );
};

